import { DateTime } from 'luxon';
import '@ionic/core';
import { Component, Element, getAssetPath, h, Prop, State, Watch } from '@stencil/core';
import '../language-chips/language-chips';

@Component({
  tag: 'clinic-information-view',
  styleUrl: 'clinic-information-view.css',
  shadow: true,
  assetsDirs: ['assets']
})
export class OpportunityCard {
    @Element() el: HTMLElement;

    @Prop({ attribute: 'cohort-id' }) opportunityCohortId: number = -104;
    @Prop() opportunityCounty: string = 'Brevard';
    @Prop({ attribute: 'opportunity-state' }) opportunityStateAbbr: string = 'FL';
    @Prop({ attribute: 'partner-name' }) opportunityPartnerName: string = 'Test Clinic Information Partner';
    @Prop({ attribute: 'partner-case-id' }) opportunityPartnerCaseId: string = '-114';
    @Prop({ attribute: 'opportunity-summary' }) opportunitySummary: string = 'Clinic <em>(IMPORTANT)</em> Information #SpeaksQ’anjob’al Summary #remote #mentorship';
    @Prop({ attribute: 'clinic-sessions' }) clinicSessions: any[] | string = [];

    private opportunityLocationName: string;
    private mapBasePath = '../../assets/maps/'

    @State() mapURL: string;
    @State() displayRemoteChip: boolean;
    @State() displayMentorshipChip: boolean;
    @State() humanClinicSessionList: any[] = [];
    @State() sessionListSummary: string = '';

    private _clinicSessions: any[];

    @Watch('clinicSessions')
    clinicSessionWatcher(newValue: any[] | string | string) {
        // console.log(`Updating clinic session summary for ${this.opportunityPartnerCaseId}`)
        this._clinicSessions = (typeof newValue === 'string') ? JSON.parse(newValue) : newValue;
        this.sessionListSummary = this.displaySessionSummary(this.findFirstAndLastSession(this._clinicSessions))
    }

    componentWillLoad() {
        this.formatLocationName(this.opportunityCounty, this.opportunityStateAbbr)
        this.generateMapURL();
    }

    componentShouldUpdate() {
        this.mapURL = getAssetPath(this.mapBasePath.concat('MapPlaceholder_260x120.png'));
        this.formatLocationName(this.opportunityCounty, this.opportunityStateAbbr)
    }

    componentWillRender() {
        this.formatLocationName(this.opportunityCounty, this.opportunityStateAbbr)
        this.generateMapURL();
        this.handleOpportunitySummaryChange(this.opportunitySummary);
        this.clinicSessionWatcher(this.clinicSessions)
    }

    @Watch('opportunitySummary')
    handleOpportunitySummaryChange(newSummary: string) {

        if (newSummary.toLowerCase().includes('#remote')) {
            this.displayRemoteChip = true;
        } else {
            this.displayRemoteChip = false;
        }

        if (newSummary.toLowerCase().includes('#mentorship')) {
            this.displayMentorshipChip = true;
        } else {
            this.displayMentorshipChip = false;
        }

        // const regexp = new RegExp('#([^\\s]*)','g');
        // this.opportunitySummary = this.opportunitySummary.replace(regexp, '');
    }

    private findFirstAndLastSession(session_list) {
        const first_last_result = {
            first_session: null,
            last_session: null,
            session_count: 0
        };
    
        try {
            first_last_result.session_count = session_list.length;
        } catch (LengthError) {
            console.error('Unable to determine the number of sessions, setting to 0.');
        }
    
        try {
            if (session_list.length === 1) {
                first_last_result.first_session = session_list[0].start_date_time;
                first_last_result.last_session = session_list[0].start_date_time;
            } else if (session_list.length >= 2) {
                session_list.forEach(session => {
                    const sessionDateTime = DateTime.fromISO(session.start_date_time, { zone: 'utc' });
                    if (first_last_result.first_session === null) {
                        first_last_result.first_session = sessionDateTime;
                    } else {
                        if (sessionDateTime < DateTime.fromISO(first_last_result.first_session, { zone: 'utc' })) {
                            first_last_result.first_session = sessionDateTime;
                        }
                    }
    
                    if (first_last_result.last_session === null) {
                        first_last_result.last_session = sessionDateTime;
                    } else {
                        if (sessionDateTime > DateTime.fromISO(first_last_result.last_session, { zone: 'utc' })) {
                            first_last_result.last_session = sessionDateTime;
                        }
                    }
                });
            }
        } catch (EvalError) {
            console.error(`Failed to evaluation session in list.`, EvalError.toString());
        }
    
        return first_last_result;
    }
    
    private displaySessionSummary(sessions_meta) {
        if (sessions_meta.session_count === 0) {
            return `There are no upcoming sessions.`
        } else {
            const humanStart = DateTime.fromISO(sessions_meta.first_session, { zone: 'utc' }).toFormat('LLL dd@t');
            const humanLast = DateTime.fromISO(sessions_meta.last_session, { zone: 'utc' }).toFormat('LLL dd@t');
    
            let sessionLabel = (sessions_meta.session_count > 1) ? 'times' : 'time';
    
            if (sessions_meta.first_session === sessions_meta.last_session) {
                return `<span class="SessionHighlight">${sessions_meta.session_count} ${sessionLabel} available<span> starting at ${humanStart}`
            } else {
                return `<span class="SessionHighlight">${sessions_meta.session_count} ${sessionLabel} available</span>: ${humanStart} to ${humanLast}`
            }
        }
    }

    private formatLocationName(countyName: string, stateName: string) {
        this.opportunityLocationName = `${this.capitalizePhrase(countyName)}, ${stateName.toUpperCase()}`;
    }

    // This function handles 's at the end of words in the phrase.
    private capitalizePhrase(input: string): string {
        return input
            .split(' ')
            .map((word) => {
                if (word.endsWith("'s")) {
                    return (
                        word.charAt(0).toUpperCase() +
                        word.slice(1, word.length - 2) +
                        word.slice(-2)
                    );
                } else {
                    return word.charAt(0).toUpperCase() + word.slice(1);
                }
            })
            .join(' ');
    }

    private generateMapURL() {
        this.mapURL = `https://api.probono.savvysuit.com/v2/maps/${this.opportunityStateAbbr}/"${this.opportunityCounty}"`
    }

    render() {
        let remoteChip;
        let mentorshipChip;

        if (this.displayRemoteChip) {
            remoteChip = <ion-chip outline><ion-icon name="call"></ion-icon><ion-label>Help Remotely</ion-label></ion-chip>;
        } else {
            remoteChip = null
        }

        if (this.displayMentorshipChip) {
            mentorshipChip = <ion-chip outline><ion-icon name="help-buoy"></ion-icon><ion-label>Mentors Avail.</ion-label></ion-chip>;
        } else {
            mentorshipChip = null;
        }

        return (
            <div>
                <div class="MapView">
                    <img src={this.mapURL} />
                    <div class="MapLocationName">{this.opportunityLocationName}</div>
                </div>
                <div class="ClearBoth">&nbsp;</div>
                <div class="NarrativeContent">
                    <div class="PartnerTitle">{this.opportunityPartnerName}</div>
                    <div class="CaseId">Case ID: {this.opportunityPartnerCaseId}</div>
                    <div class="CaseDescription" innerHTML={this.opportunitySummary.substr(0, 350)}></div>
                    <ion-chip outline><ion-icon name="people-circle"></ion-icon><ion-label>Clinic</ion-label></ion-chip>
                    {remoteChip}
                    {mentorshipChip}
                    <language-chips cardSummary={this.opportunitySummary}></language-chips>
                    <div class="ClearBoth">&nbsp;</div>
                    <div class="SessionList"><span innerHTML={this.sessionListSummary}></span></div>
                    <div class="ClearBoth">&nbsp;</div>
                </div>
            </div>
        );
    }
}
